import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { 
  Users, 
  Shield, 
  MoreVertical, 
  Trash2,
  Crown,
  UserCheck,
  Mail
} from "lucide-react";

const roleConfig = {
  admin: { label: "Administrateur", className: "bg-amber-900/50 text-amber-300 border-amber-800", icon: Crown },
  gestionnaire: { label: "Gestionnaire", className: "bg-blue-900/50 text-blue-300 border-blue-800", icon: UserCheck },
};

export default function Utilisateurs() {
  const [users, setUsers] = useState([]);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roleRes, usersRes] = await Promise.all([
        apiClient.get("/users/me/role"),
        apiClient.get("/users").catch(() => ({ data: [] })),
      ]);
      setCurrentUserRole(roleRes.data);
      setUsers(usersRes.data || []);
    } catch (error) {
      // Non-admin users can't see user list
      try {
        const roleRes = await apiClient.get("/users/me/role");
        setCurrentUserRole(roleRes.data);
      } catch (e) {
        console.error(e);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSetAdmin = async (userId) => {
    try {
      await apiClient.post(`/users/${userId}/set-admin`);
      toast.success("Utilisateur promu administrateur");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la promotion");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
    try {
      await apiClient.delete(`/users/${userId}`);
      toast.success("Utilisateur supprimé");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-zinc-400">Chargement...</div>
      </div>
    );
  }

  const isAdmin = currentUserRole?.is_admin;

  return (
    <div className="space-y-6" data-testid="utilisateurs-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-heading font-bold text-white tracking-tight">Utilisateurs</h1>
        <p className="text-zinc-500 mt-1">Gestion des comptes et des rôles</p>
      </div>

      {/* Current user role */}
      <Card className="bg-[#121214] border-zinc-800">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-600/20 flex items-center justify-center">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-zinc-500">Votre rôle</p>
                <div className="flex items-center gap-2">
                  <p className="text-lg font-medium text-white">
                    {roleConfig[currentUserRole?.role]?.label || "Utilisateur"}
                  </p>
                  <Badge variant="outline" className={roleConfig[currentUserRole?.role]?.className}>
                    {currentUserRole?.role}
                  </Badge>
                </div>
              </div>
            </div>
            {isAdmin && (
              <Badge className="bg-amber-900/50 text-amber-300 border-amber-800">
                <Crown className="w-3 h-3 mr-1" />
                Accès complet
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Admin Only Section */}
      {!isAdmin ? (
        <Card className="bg-[#121214] border-zinc-800">
          <CardContent className="py-12 text-center">
            <Shield className="w-12 h-12 mx-auto text-zinc-700 mb-4" />
            <p className="text-zinc-400 font-medium">Accès restreint</p>
            <p className="text-zinc-500 text-sm mt-1">
              La gestion des utilisateurs est réservée aux administrateurs
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-[#121214] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500">Total utilisateurs</p>
                    <p className="text-3xl font-heading font-bold text-white">{users.length}</p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121214] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500">Administrateurs</p>
                    <p className="text-3xl font-heading font-bold text-amber-400">
                      {users.filter(u => u.role === "admin").length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-amber-600/20 flex items-center justify-center">
                    <Crown className="w-5 h-5 text-amber-400" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#121214] border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-widest text-zinc-500">Gestionnaires</p>
                    <p className="text-3xl font-heading font-bold text-blue-400">
                      {users.filter(u => u.role === "gestionnaire" || u.role === "user").length}
                    </p>
                  </div>
                  <div className="w-10 h-10 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-blue-400" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Users List */}
          <Card className="bg-[#121214] border-zinc-800">
            <CardHeader>
              <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-zinc-500" />
                Liste des utilisateurs
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {users.map((user) => {
                  const RoleIcon = roleConfig[user.role]?.icon || UserCheck;
                  
                  return (
                    <div 
                      key={user.id} 
                      className="flex items-center justify-between p-4 rounded-lg bg-zinc-900/50 border border-zinc-800"
                      data-testid={`user-card-${user.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                          <span className="text-sm font-medium text-zinc-300">
                            {user.nom?.charAt(0)?.toUpperCase() || "U"}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-white">{user.nom}</p>
                          <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <Badge variant="outline" className={roleConfig[user.role]?.className || "border-zinc-700 text-zinc-400"}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleConfig[user.role]?.label || user.role}
                        </Badge>
                        
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-zinc-400 hover:text-white">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="bg-[#121214] border-zinc-800">
                            {user.role !== "admin" && (
                              <DropdownMenuItem 
                                onClick={() => handleSetAdmin(user.id)}
                                className="text-amber-400 focus:bg-zinc-800"
                              >
                                <Crown className="w-4 h-4 mr-2" />
                                Promouvoir admin
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator className="bg-zinc-800" />
                            <DropdownMenuItem 
                              onClick={() => handleDeleteUser(user.id)}
                              className="text-red-400 focus:bg-zinc-800"
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
